import React from 'react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Dropdown, DropdownItem, DropdownContainer } from '../../Dropdown';
import { FormLabel } from '../../FormLabel';
import FileUploadField from './FileUploadField';
import { AttachedFile } from '../Files';
import type { NewCommentFile, FileType, InsuranceOption } from '../../../api';

export type InsuranceCardSectionProps = {
  /**
   * The currently selected insurance option
   */
  insuranceOptionSelected: InsuranceOption;
  /**
   * Callback when insurance option changes
   */
  setInsuranceOption: (insuranceOption: InsuranceOption) => void;
  /**
   * Whether the task is completed (view-only mode)
   */
  isTaskCompleted: boolean;
  /**
   * Whether to show "use existing insurance on file" option
   */
  showInsuranceOnFile: boolean;
  /**
   * Insurance card files uploaded by user
   */
  insuranceCardFiles: NewCommentFile[];
  /**
   * Callback to delete a file
   */
  handleFileDelete: (filename: string, document_type: FileType) => void;
  /**
   * Callback to upload files
   */
  handleFileUpload: (newFiles: NewCommentFile[]) => void;
  /**
   * IDs of uploaded files (for completed tasks)
   */
  uploadedFileIds: string[];
  /**
   * Whether to show validation error for missing insurance
   */
  showMissingInsuranceError: boolean;
  /**
   * File validation error message
   */
  fileValidationError: string | null;
};

/**
 * Component for selecting insurance option and uploading insurance cards.
 * Used in task views that need insurance information (e.g., DocumentUploadTaskView, GiaSubQTaskView).
 */
export const InsuranceCardSection: React.FC<InsuranceCardSectionProps> = ({
  insuranceOptionSelected,
  setInsuranceOption,
  isTaskCompleted,
  showInsuranceOnFile,
  insuranceCardFiles,
  handleFileDelete,
  handleFileUpload,
  uploadedFileIds,
  showMissingInsuranceError,
  fileValidationError,
}) => (
  <div className="flex flex-1 flex-col justify-center gap-6">
    <DropdownContainer>
      <FormLabel id="insurance-option-label">Insurance option</FormLabel>
      <Dropdown
        labelId="insurance-option-label"
        id="insurance-option"
        value={insuranceOptionSelected}
        onChange={(event) => {
          setInsuranceOption(event.target.value as InsuranceOption);
        }}
        disabled={isTaskCompleted}
        $hasError={showMissingInsuranceError}
      >
        {showInsuranceOnFile && (
          <DropdownItem value="use_existing_insurance">Use existing insurance on file</DropdownItem>
        )}
        <DropdownItem value="new_insurance">Upload new insurance</DropdownItem>
        <DropdownItem value="no_insurance">Patient is self pay / uninsured</DropdownItem>
        <DropdownItem value="insurance_unavailable">Insurance not available</DropdownItem>
      </Dropdown>
    </DropdownContainer>

    {insuranceOptionSelected === 'new_insurance' && (
      <>
        {isTaskCompleted ? (
          <div className="flex flex-col gap-3">
            <Typography styledAs="bodySmallDMSans">
              Please upload an image of the front and back of the card.
            </Typography>
            {uploadedFileIds.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {uploadedFileIds.map((fileId: string) => (
                  <AttachedFile key={fileId} fileId={fileId} />
                ))}
              </div>
            ) : (
              <Typography styledAs="bodySmallDMSans">No insurance card uploaded</Typography>
            )}
          </div>
        ) : (
          <FileUploadField
            instructions="Please upload an image of the front and back of the card."
            uploadedFiles={insuranceCardFiles}
            onFileDelete={handleFileDelete}
            onFilesUpload={handleFileUpload}
            fileTypesOptions={['Insurance Card Both Sides']}
            errors={[
              {
                message: 'Please upload patient insurance or select a different option from the insurance dropdown',
                visible: showMissingInsuranceError,
              },
              {
                message: fileValidationError || '',
                visible: !!fileValidationError,
              },
            ]}
          />
        )}
      </>
    )}
  </div>
);
